import { useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import * as XLSX from 'xlsx'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import SendIcon from '@mui/icons-material/Send'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ConfirmDialog from '../../Shared/ConfirmDialog'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { exportToExcel } from '../../../utils/export'
import { formatBRL } from '../../../utils/format'
import { createBulkProduct } from '../../../services/productService'
import { mensagemDeErro } from '../../../services/apiClient'
import PermissionComponent from '../../../permissions'
import { PERMISSOES } from '../../../const/permissoes'

/** A rota aceita até 1000 itens por chamada; 500 mantém a resposta rápida. */
const BATCH_SIZE = 500

/**
 * Colunas da planilha, na mesma ordem e com os MESMOS cabeçalhos que a
 * exportação da tela de Produtos gera. É o que faz o ciclo exportar → corrigir
 * no Excel → reimportar funcionar sem renomear coluna nenhuma.
 */
const TEMPLATE_COLUMNS = [
  { header: 'Nome', accessor: 'name' },
  { header: 'Descrição', accessor: 'description' },
  { header: 'Identificação ERP', accessor: 'idProductPartner' },
  { header: 'Referência', accessor: 'reference' },
  { header: 'SKU', accessor: 'sku' },
  { header: 'Tipo Produto', accessor: 'typeProduct' },
  { header: 'Marca', accessor: 'brand' },
  { header: 'Fabricante', accessor: 'supplier' },
  { header: 'Preço', accessor: 'price' },
  { header: 'Cód. Barras', accessor: 'barCode' },
  { header: 'Cor', accessor: 'color' },
  { header: 'Tamanho', accessor: 'size' },
  { header: 'Informação Adicional', accessor: 'info' },
  { header: 'Ativo', accessor: row => (row.active === false ? 'Não' : 'Sim') },
  { header: 'Qtd. por Embalagem', accessor: 'piece' },
  { header: 'Tipo de Peça', accessor: 'typePiece' },
]

/**
 * Cabeçalhos aceitos por campo, já normalizados (sem acento, só letras/números).
 *
 * Cada campo aceita o rótulo em português E o nome do contrato da API: a mesma
 * planilha serve para quem exporta da tela e para quem gera do ERP. `referencia`
 * e `informacaoadicional` também respondem pelos nomes antigos do sistema
 * legado (`referencia`, `informationAdditional`), que ainda circulam em arquivo.
 */
const FIELD_ALIASES = {
  name: ['name', 'nome', 'nomedoproduto', 'produto', 'descricaodoproduto'],
  description: ['description', 'descricao'],
  idProductPartner: [
    'idproductpartner',
    'identificacaoerp',
    'iderp',
    'codigoerp',
    'codigodoparceiro',
  ],
  reference: ['reference', 'referencia', 'ref'],
  sku: ['sku', 'codigo', 'codigodoproduto'],
  typeProduct: ['typeproduct', 'tipoproduto', 'tipodoproduto'],
  brand: ['brand', 'marca'],
  supplier: ['supplier', 'fabricante', 'fornecedor'],
  price: ['price', 'preco', 'precounitario', 'valor'],
  barCode: ['barcode', 'codbarras', 'codigodebarras', 'ean'],
  color: ['color', 'cor'],
  size: ['size', 'tamanho'],
  info: ['info', 'informacaoadicional', 'informationadditional', 'observacao', 'obs'],
  active: ['active', 'ativo', 'situacao'],
  piece: ['piece', 'qtdporembalagem', 'quantidadeporembalagem', 'pecasporembalagem', 'embalagem'],
  typePiece: ['typepiece', 'tipodepeca', 'tipopeca', 'unidade'],
}

const AO_DUPLICAR_OPCOES = [
  {
    value: 'recusar',
    label: 'Recusar a linha',
    hint: 'O produto já cadastrado não é tocado e a linha volta com erro.',
  },
  {
    value: 'atualizar',
    label: 'Atualizar o cadastro',
    hint: 'Só os campos preenchidos na planilha são gravados; os demais ficam como estão.',
  },
  {
    value: 'ignorar',
    label: 'Ignorar a linha',
    hint: 'A linha é pulada em silêncio, sem alterar nem contar como erro.',
  },
]

/** NFD separa os acentos e o filtro final descarta tudo que não é letra/número. */
const normalizeHeader = value =>
  String(value === null || value === undefined ? '' : value)
    .normalize('NFD')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const pickField = (sheetRow, field) => {
  const key = Object.keys(sheetRow).find(name =>
    FIELD_ALIASES[field].includes(normalizeHeader(name))
  )
  if (!key) return ''
  const value = sheetRow[key]
  return value === null || value === undefined ? '' : String(value).trim()
}

/**
 * Preço em pt-BR ou en-US.
 *
 * `1.234,56` e `1234.56` são o mesmo número em planilhas diferentes, e o legado
 * guardava preço como texto — então vem de tudo. A vírgula decide: havendo
 * vírgula, o ponto é separador de milhar.
 */
const parsePreco = texto => {
  if (!texto) return undefined
  const limpo = texto.replace(/[^\d,.-]/g, '')
  // Sem nenhum dígito não é preço: `Number('')` é 0, e um "dez reais" na
  // planilha entraria calado como R$ 0,00 em vez de ser recusado.
  if (!/\d/.test(limpo)) return null
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo
  const numero = Number(normalizado)
  return Number.isFinite(numero) && numero >= 0 ? numero : null
}

const parseInteiro = texto => {
  if (!texto) return undefined
  const numero = Number(String(texto).replace(/[^\d-]/g, ''))
  return Number.isInteger(numero) && numero > 0 ? numero : null
}

const VERDADEIRO = ['sim', 's', 'true', 'verdadeiro', '1', 'ativo']
const FALSO = ['nao', 'n', 'false', 'falso', '0', 'inativo']

const parseAtivo = texto => {
  if (!texto) return undefined
  const valor = normalizeHeader(texto)
  if (VERDADEIRO.includes(valor)) return true
  if (FALSO.includes(valor)) return false
  return null
}

const TIPOS_DE_PECA = { peca: 'PECA', pecas: 'PECA', un: 'PECA', caixa: 'CAIXA', cx: 'CAIXA', kg: 'KG' }

const parseTipoDePeca = texto => {
  if (!texto) return undefined
  return TIPOS_DE_PECA[normalizeHeader(texto)] ?? null
}

/**
 * Linha da planilha → corpo da API.
 *
 * Campo em branco vira `undefined` e **não é enviado**: em modo "atualizar", o
 * que não veio na planilha não pode apagar o que já está cadastrado. Valor
 * inválido vira `null` e a linha é barrada antes do envio, com o motivo — é
 * melhor recusar aqui do que deixar o 400 do lote inteiro explicar.
 */
const parseRow = sheetRow => {
  const texto = campo => pickField(sheetRow, campo) || undefined

  return {
    name: texto('name'),
    description: texto('description'),
    idProductPartner: texto('idProductPartner'),
    reference: texto('reference'),
    sku: texto('sku'),
    typeProduct: texto('typeProduct'),
    brand: texto('brand'),
    supplier: texto('supplier'),
    price: parsePreco(pickField(sheetRow, 'price')),
    barCode: texto('barCode'),
    color: texto('color'),
    size: texto('size'),
    info: texto('info'),
    active: parseAtivo(pickField(sheetRow, 'active')),
    piece: parseInteiro(pickField(sheetRow, 'piece')),
    typePiece: parseTipoDePeca(pickField(sheetRow, 'typePiece')),
  }
}

/** Motivo da recusa local, ou string vazia quando a linha está boa. */
const validaLinha = item => {
  if (!item.name) return 'Informe o nome do produto.'
  if (item.price === null) return 'Preço inválido — use apenas números (ex.: 1.234,56).'
  if (item.piece === null) return 'Quantidade por embalagem deve ser um número inteiro positivo.'
  if (item.typePiece === null) return 'Tipo de peça deve ser Peça, Caixa ou KG.'
  if (item.active === null) return 'Ativo deve ser Sim ou Não.'
  if (item.typePiece === 'CAIXA' && !item.piece) {
    return 'Produto do tipo Caixa precisa da quantidade por embalagem.'
  }
  return ''
}

/** Só o que a API declara: `undefined` fora, para não sobrescrever com vazio. */
const paraEnvio = item =>
  Object.fromEntries(
    Object.entries(item).filter(
      ([chave, valor]) =>
        !['id', 'status', 'error'].includes(chave) && valor !== undefined && valor !== null
    )
  )

const chunk = (list, size) => {
  const parts = []
  for (let index = 0; index < list.length; index += size) {
    parts.push(list.slice(index, index + size))
  }
  return parts
}

const STATUS_CHIP = {
  sending: { color: 'default', label: 'Enviando...' },
  inserido: { color: 'success', label: 'Inserido' },
  atualizado: { color: 'info', label: 'Atualizado' },
  ignorado: { color: 'warning', label: 'Ignorado' },
  pending: { color: 'info', label: 'Pronto para enviar' },
}

const StatusChip = ({ item }) => {
  if (item.status === 'sending') {
    return (
      <Chip
        size="small"
        color="default"
        icon={<CircularProgress size={12} sx={{ ml: 1 }} />}
        label="Enviando..."
      />
    )
  }
  if (item.status === 'error' || item.status === 'invalid') {
    return (
      <Tooltip title={item.error}>
        <Chip
          size="small"
          color="error"
          icon={<ErrorOutlineIcon />}
          label={item.error}
          sx={{ maxWidth: 320 }}
        />
      </Tooltip>
    )
  }
  const chip = STATUS_CHIP[item.status] || STATUS_CHIP.pending
  if (item.status === 'ignorado') {
    return (
      <Tooltip title={item.error || ''}>
        <Chip size="small" color={chip.color} label={chip.label} />
      </Tooltip>
    )
  }
  return <Chip size="small" color={chip.color} label={chip.label} />
}

/**
 * Carga de produtos em lote.
 *
 * Diferente da Carga de Receita de Máquina, aqui **não há seletor de filial**:
 * produto é escopado por EMPRESA (`EfProduct` nunca teve `SubCompanyId`), e a
 * empresa sai do perfil do SSO — o operador não escolhe.
 *
 * Também não há formulário de cadastro manual: são dezesseis campos, e a tela
 * de Produtos já tem o diálogo de criar um a um.
 */
const ChargeProduct = () => {
  const { notify, FeedbackSnackbar } = useFeedback()

  const nextId = useRef(0)
  const fileInputRef = useRef(null)

  const [items, setItems] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [aoDuplicar, setAoDuplicar] = useState('recusar')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const [pendingSend, setPendingSend] = useState(null)
  const [clearOpen, setClearOpen] = useState(false)

  const counters = useMemo(
    () => ({
      total: items.length,
      selected: selectedIds.length,
      gravados: items.filter(item => ['inserido', 'atualizado'].includes(item.status)).length,
      ignorados: items.filter(item => item.status === 'ignorado').length,
      error: items.filter(item => ['error', 'invalid'].includes(item.status)).length,
    }),
    [items, selectedIds]
  )

  const pagedItems = useMemo(
    () => items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  )

  const handleFile = async event => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!file) return

    try {
      const buffer = await file.arrayBuffer()
      // `raw` desliga o palpite de tipo do leitor. Sem ele, um preço em pt-BR
      // vira outro número no CSV: `1.234,56` é lido como 1.234 e `99,90` como
      // 9990 — a vírgula é tratada como separador de milhar. O texto original
      // chega aqui e quem decide é `parsePreco`, que conhece os dois formatos.
      const workbook = XLSX.read(buffer, { type: 'array', raw: true })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      if (!sheet) {
        notify('A planilha não possui abas legíveis.', 'error')
        return
      }

      const sheetRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true })
      if (sheetRows.length === 0) {
        notify('A planilha está vazia.', 'warning')
        return
      }

      // Linha inválida entra na lista marcada em vermelho, não é descartada: o
      // operador precisa ver qual linha da planilha dele está errada e por quê.
      const novos = sheetRows.map(sheetRow => {
        const item = parseRow(sheetRow)
        const motivo = validaLinha(item)
        nextId.current += 1
        return {
          ...item,
          id: nextId.current,
          status: motivo ? 'invalid' : 'pending',
          error: motivo,
        }
      })

      setItems(current => [...current, ...novos])
      setSelectedIds(current => [
        ...current,
        ...novos.filter(item => item.status === 'pending').map(item => item.id),
      ])
      setPage(0)

      const invalidas = novos.filter(item => item.status === 'invalid').length
      notify(
        invalidas > 0
          ? `${novos.length} linha(s) carregada(s), ${invalidas} com problema para corrigir.`
          : `${novos.length} linha(s) carregada(s).`,
        invalidas > 0 ? 'warning' : 'success'
      )
    } catch (error) {
      notify('Não foi possível ler a planilha selecionada.', 'error')
    }
  }

  const downloadTemplate = () => {
    const exemplo = [
      {
        name: 'Produto de exemplo',
        description: 'Descrição do produto',
        idProductPartner: 'ERP-0001',
        reference: 'REF-0001',
        sku: 'SKU-0001',
        typeProduct: 'Acabado',
        brand: 'Marca',
        supplier: 'Fabricante',
        price: '1.234,56',
        barCode: '7891234567890',
        color: 'Azul',
        size: 'M',
        info: 'Observações',
        active: true,
        piece: 12,
        typePiece: 'CAIXA',
      },
    ]
    exportToExcel(exemplo, TEMPLATE_COLUMNS, 'modelo-carga-produtos', 'Modelo')
  }

  const exportarPendencias = () => {
    const comProblema = items.filter(item => ['error', 'invalid'].includes(item.status))
    if (comProblema.length === 0) {
      notify('Nenhuma linha com erro para exportar.', 'info')
      return
    }
    exportToExcel(
      comProblema,
      [...TEMPLATE_COLUMNS, { header: 'Motivo', accessor: 'error' }],
      'produtos-com-erro',
      'Erros'
    )
  }

  const toggleItem = id =>
    setSelectedIds(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    )

  const selecionaveis = useMemo(
    () => items.filter(item => item.status !== 'invalid').map(item => item.id),
    [items]
  )

  const toggleAll = () =>
    setSelectedIds(current => (current.length === selecionaveis.length ? [] : selecionaveis))

  const removeItem = id => {
    setItems(current => current.filter(item => item.id !== id))
    setSelectedIds(current => current.filter(item => item !== id))
  }

  const clearList = () => {
    setItems([])
    setSelectedIds([])
    setPage(0)
    setProgress({ done: 0, total: 0 })
    setClearOpen(false)
  }

  const setBatchStatus = (batchIds, status, error = '') => {
    const idSet = new Set(batchIds)
    setItems(current =>
      current.map(item => (idSet.has(item.id) ? { ...item, status, error } : item))
    )
  }

  /**
   * A rota responde 200 mesmo com linhas recusadas — por contrato ela não aborta
   * no primeiro erro. O desfecho vem em `results`, ligado à linha pelo `index`
   * (posição no array enviado), e não pela chave de negócio: nome e SKU se
   * repetem em planilha de produto, e casar por chave marcaria a linha errada.
   */
  const aplicaResultado = (lote, results) => {
    const porId = new Map()
    results.forEach(resultado => {
      const enviado = lote[resultado.index]
      if (enviado) porId.set(enviado.id, resultado)
    })
    setItems(current =>
      current.map(item => {
        const resultado = porId.get(item.id)
        if (!resultado) return item
        return {
          ...item,
          status: resultado.status === 'erro' ? 'error' : resultado.status,
          error: resultado.motivo || '',
        }
      })
    )
  }

  const runSend = async targets => {
    setSending(true)
    setProgress({ done: 0, total: targets.length })

    let done = 0
    let comErro = 0

    for (const lote of chunk(targets, BATCH_SIZE)) {
      const batchIds = lote.map(item => item.id)
      setBatchStatus(batchIds, 'sending')

      try {
        const response = await createBulkProduct(lote.map(paraEnvio), aoDuplicar)
        comErro += response.data?.failed || 0
        aplicaResultado(lote, response.data?.results || [])
      } catch (error) {
        comErro += lote.length
        setBatchStatus(batchIds, 'error', mensagemDeErro(error, 'Falha ao enviar o lote.'))
      }

      done += lote.length
      setProgress({ done, total: targets.length })
    }

    setSending(false)
    if (comErro === 0) {
      notify(`${targets.length} produto(s) processado(s) com sucesso!`, 'success')
    } else {
      notify(`Carga concluída com ${comErro} linha(s) em erro.`, 'warning')
    }
  }

  const requestSend = scope => {
    const candidatos = items.filter(item => item.status !== 'invalid')
    const targets =
      scope === 'selected' ? candidatos.filter(item => selectedIds.includes(item.id)) : candidatos
    if (targets.length === 0) {
      notify('Nenhuma linha válida para enviar.', 'warning')
      return
    }
    setPendingSend(targets)
  }

  const confirmSend = () => {
    const targets = pendingSend
    setPendingSend(null)
    runSend(targets)
  }

  const allSelected = selecionaveis.length > 0 && selectedIds.length === selecionaveis.length

  const columns = [
    {
      id: 'select',
      width: 60,
      label: (
        <Checkbox
          size="small"
          sx={{ color: 'common.white', '&.Mui-checked': { color: 'common.white' } }}
          checked={allSelected}
          indeterminate={selectedIds.length > 0 && !allSelected}
          disabled={selecionaveis.length === 0 || sending}
          onChange={toggleAll}
        />
      ),
      element: row => (
        <Checkbox
          size="small"
          checked={selectedIds.includes(row.id)}
          disabled={sending || row.status === 'invalid'}
          onChange={() => toggleItem(row.id)}
        />
      ),
    },
    { id: 'name', label: 'Nome', element: row => row.name || '—' },
    { id: 'sku', label: 'SKU', element: row => row.sku || '—' },
    { id: 'reference', label: 'Referência', element: row => row.reference || '—' },
    { id: 'brand', label: 'Marca', element: row => row.brand || '—' },
    {
      id: 'price',
      label: 'Preço',
      align: 'right',
      element: row => (row.price === undefined ? '—' : formatBRL(row.price)),
    },
    { id: 'status', label: 'Status', element: row => <StatusChip item={row} /> },
    {
      id: 'actions',
      label: 'Ações',
      width: 80,
      element: row => (
        <Tooltip title="Remover da lista">
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={sending}
              onClick={() => removeItem(row.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ]

  const progressPercent =
    progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)

  const hintDuplicado = AO_DUPLICAR_OPCOES.find(opcao => opcao.value === aoDuplicar)?.hint

  return (
    <Box>
      <PageHeader
        title="Carga de Produtos"
        subtitle="Importe o cadastro de produtos em lote a partir de uma planilha"
        actions={
          <>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
              Baixar modelo
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={counters.error === 0}
              onClick={exportarPendencias}
            >
              Baixar erros
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              disabled={items.length === 0 || sending}
              onClick={() => setClearOpen(true)}
            >
              Limpar lista
            </Button>
          </>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Importar planilha
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Formatos aceitos: .xls, .xlsx e .csv. A primeira aba é lida e os cabeçalhos são
          reconhecidos pelo nome — os mesmos da exportação da tela de Produtos, então dá para
          exportar, corrigir no Excel e reimportar. Só <strong>Nome</strong> é obrigatório. O
          produto é gravado na empresa do seu usuário; não há escolha de filial.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          hidden
          onChange={handleFile}
        />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={sending}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              Selecionar planilha
            </Button>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ao-duplicar-label">Se o produto já existir</InputLabel>
              <Select
                labelId="ao-duplicar-label"
                label="Se o produto já existir"
                value={aoDuplicar}
                disabled={sending}
                onChange={event => setAoDuplicar(event.target.value)}
              >
                {AO_DUPLICAR_OPCOES.map(opcao => (
                  <MenuItem key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              {hintDuplicado} O produto é reconhecido pelo SKU — ou pela identificação de ERP,
              quando a linha não traz SKU.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${counters.total}`} />
          <Chip label={`Selecionados: ${counters.selected}`} color="primary" variant="outlined" />
          <Chip label={`Gravados: ${counters.gravados}`} color="success" variant="outlined" />
          <Chip label={`Ignorados: ${counters.ignorados}`} color="warning" variant="outlined" />
          <Chip label={`Com erro: ${counters.error}`} color="error" variant="outlined" />
        </Box>
        <PermissionComponent permissao={PERMISSOES.PRODUTO_GERENCIAR}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={sending || counters.selected === 0}
              onClick={() => requestSend('selected')}
            >
              Enviar selecionados
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={sending || counters.total === counters.error}
              onClick={() => requestSend('all')}
            >
              Enviar lista
            </Button>
          </Box>
        </PermissionComponent>
      </Box>

      {sending && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={progressPercent} />
          <Typography variant="caption" color="text.secondary">
            {`${progress.done} de ${progress.total} enviados (${progressPercent}%)`}
          </Typography>
        </Box>
      )}

      <DataTable
        columns={columns}
        rows={pagedItems}
        page={page}
        rowsPerPage={rowsPerPage}
        total={items.length}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowKey={row => row.id}
        emptyMessage="Nenhuma linha na lista. Importe uma planilha para começar."
      />

      <ConfirmDialog
        open={Boolean(pendingSend)}
        title="Enviar carga de produtos"
        description={
          pendingSend
            ? `Confirma o envio de ${pendingSend.length} linha(s) em lotes de ${BATCH_SIZE}? ` +
              `Produto já existente será: ${
                AO_DUPLICAR_OPCOES.find(opcao => opcao.value === aoDuplicar)?.label.toLowerCase() ||
                ''
              }.`
            : ''
        }
        confirmLabel="Enviar"
        confirmColor="primary"
        onConfirm={confirmSend}
        onClose={() => setPendingSend(null)}
      />

      <ConfirmDialog
        open={clearOpen}
        title="Limpar lista"
        description="Tem certeza que deseja remover todas as linhas da lista?"
        confirmLabel="Limpar"
        onConfirm={clearList}
        onClose={() => setClearOpen(false)}
      />

      <FeedbackSnackbar />
    </Box>
  )
}

export default ChargeProduct
